// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import IconPaginationArrowLeft from "@conklin/XDL/Icons/IconPaginationArrowLeft";
import IconPaginationArrowRight from "@conklin/XDL/Icons/IconPaginationArrowRight";
import { color } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconProps, IconWrapper } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Ref, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import styled, { css, ThemeProps } from "styled-components";

export interface FancyScrollRefProps {
    updateScrollable: () => void;
}

export interface FancyScrollProps {
    children: React.ReactElement;
    refElement: React.RefObject<HTMLElement | null>;
    wrapperRef?: React.RefObject<HTMLElement | null>;
    showArrow?: boolean;
    extendedStyles?: InjectableCss;
    color?: string;
    scrollByValue?: number;
    shadowHeight?: string;
    isBoxShadow?: boolean;
    paddingRight?: number;
    contentPaddingRight?: number;
}

export interface FancyScrollStyle {
    showArrowWrapper?: InjectableCss;
    showArrowUpIcon?: IconProps;
    showArrowDownIcon?: IconProps;
}

export const fancyScrollStyle: FancyScrollStyle = {
    showArrowWrapper: {
        css: css`
            height: 38px;
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            &:hover ${IconWrapper} {
                &,
                svg {
                    color: ${color("common.ui9")};
                }
            }
        `,
    },
    showArrowUpIcon: {
        size: 20,
        color: "common.ui9",
        css: css`
            margin: auto;
            display: block;
            transform: rotate(90deg);

            &,
            & svg {
                height: auto;
            }
        `,
    },
    showArrowDownIcon: {
        size: 20,
        color: "common.ui9",
        css: css`
            margin: auto;
            display: block;
            transform: rotate(-90deg);

            &,
            & svg {
                height: auto;
            }
        `,
    },
};
const styles = fancyScrollStyle;

const ScrollIndicator = styled.span<
    {
        direction: "up" | "down" | "left" | "right";
        transitionState: TransitionStatus;
        color?: string;
        shadowHeight?: string;
        showArrow?: boolean;
    } & ThemeProps<BaseTheme>
>`
    position: absolute;
    width: calc(100% - 8px);
    height: ${({ shadowHeight }) => shadowHeight || "12px"};
    z-index: 1;
    color: ${getColor("common.ui1")};
    display: flex;
    align-items: end;
    justify-content: center;
    transition: all ease-in-out 250ms;
    ${({ transitionState }) => {
        switch (transitionState) {
            case "entering":
                return "opacity: 0;";
            case "entered":
                return "opacity: 1;";
            case "exiting":
            case "exited":
                return "opacity: 0;";
            default:
                return "";
        }
    }}
    ${({ direction, color: customColor, shadowHeight = "12px", showArrow = false }) => {
        if (direction === "up") {
            if (!showArrow) {
                return css`
                    top: 0;
                    align-items: start;
                    background: linear-gradient(
                        180deg,
                        ${customColor ? color(customColor, 1) : color("common.white", 1)} 0%,
                        ${customColor ? color(customColor, 0) : color("common.white", 0)} 100%
                    );
                `;
            }
            return css`
                top: 0;
                background-color: ${color("common.ui6", 0.75)};
                height: 38px;
                align-items: center;
            `;
        }
        if (direction === "down") {
            if (!showArrow) {
                return css`
                    bottom: 0;
                    background: linear-gradient(
                        0deg,
                        ${customColor ? color(customColor, 1) : customColor || color("common.white", 1)} 0%,
                        ${customColor ? color(customColor, 0) : customColor || color("common.white", 0)} 100%
                    );
                `;
            }
            return css`
                bottom: 0;
                background-color: ${color("common.ui6", 0.75)};
                height: 38px;
                align-items: center;
            `;
        }
        if (direction === "left") {
            return css`
                left: 0;
                top: 0;
                width: ${shadowHeight};
                height: calc(100% - 8px);
                & ~ button > span[direction="down"] {
                    bottom: 8px; /* scroll x width in case we have it */
                }
                background: linear-gradient(
                    90deg,
                    ${customColor ? color(customColor, 1) : customColor || color("common.white", 1)} 0%,
                    ${customColor ? color(customColor, 0) : customColor || color("common.white", 0)} 100%
                );
            `;
        }
        if (direction === "right") {
            return css`
                right: 0px;
                top: 0;
                width: ${shadowHeight};
                height: calc(100% - 8px);
                & ~ button > span[direction="down"] {
                    bottom: 8px; /* scroll x width in case we have it */
                }
                &:has(~ button > span[direction="up"]),
                &:has(~ button > span[direction="down"]) {
                    right: 8px; /* scroll y exist */
                }
                background: linear-gradient(
                    270deg,
                    ${customColor ? color(customColor, 1) : customColor || color("common.white", 1)} 0%,
                    ${customColor ? color(customColor, 0) : customColor || color("common.white", 0)} 100%
                );
            `;
        }
    }}
`;

const sum = (...a: number[]) => Math.ceil(a.reduce((previousValue, currentValue) => previousValue + currentValue, 0));

const FancyScroll = (
    {
        children,
        refElement,
        wrapperRef,
        showArrow,
        extendedStyles,
        color,
        scrollByValue = 150,
        shadowHeight,
        isBoxShadow = false,
        paddingRight,
        contentPaddingRight,
    }: FancyScrollProps,
    ref: Ref<{ updateScrollable: () => void }>,
) => {
    const [showScrollDown, setShowScrollDown] = useState(false);
    const [showScrollUp, setShowScrollUp] = useState(false);
    const [showScrollRight, setShowScrollRight] = useState(false);
    const [showScrollLeft, setShowScrollLeft] = useState(false);

    const transitionElementLeft = React.useRef(null);
    const transitionElementRight = React.useRef(null);
    const transitionElementUp = React.useRef(null);
    const transitionElementDown = React.useRef(null);

    useImperativeHandle(ref, () => ({
        updateScrollable() {
            setTimeout(() => checkScrollable());
        },
    }));

    const updateParentPadding = ({ hasVScroll }: { hasVScroll?: boolean } = {}) => {
        // if we need to stretch the body to remove padding
        if (hasVScroll) {
            if (wrapperRef?.current && paddingRight !== undefined) {
                wrapperRef.current.style.paddingRight = `${paddingRight}px`;
            }
            if (refElement.current && contentPaddingRight !== undefined) {
                refElement.current.style.paddingRight = `${contentPaddingRight}px`;
            }
        } else {
            if (wrapperRef?.current && paddingRight !== undefined) {
                wrapperRef.current.style.removeProperty("padding-right");
            }
            if (refElement.current && contentPaddingRight !== undefined) {
                refElement.current.style.removeProperty("padding-right");
            }
        }
    };
    const checkScrollable = useCallback(() => {
        if (refElement.current) {
            const isFirefox = navigator?.userAgent?.indexOf("Firefox/") > -1;
            const overflow = refElement.current.style.overflow;
            if (isFirefox) {
                refElement.current.style.overflow = "scroll"; // scrollHeight === clientHeight
            }
            if (
                refElement.current.scrollHeight > refElement.current.offsetHeight &&
                refElement.current.scrollTop > 30
            ) {
                setShowScrollUp(true);
                updateParentPadding({ hasVScroll: true });
            } else {
                setShowScrollUp(false);
                updateParentPadding(); // reset
            }
            if (refElement.current.scrollHeight > sum(refElement.current.offsetHeight + refElement.current.scrollTop)) {
                setShowScrollDown(true);
                updateParentPadding({ hasVScroll: true });
            } else {
                setShowScrollDown(false);
                updateParentPadding(); // reset
            }
            if (isBoxShadow) {
                // In case we need x scrolling shadow
                if (refElement.current.scrollLeft > 30) {
                    setShowScrollLeft(true);
                } else {
                    setShowScrollLeft(false);
                }
                if (refElement.current.scrollWidth > refElement.current.scrollLeft + refElement.current.offsetWidth) {
                    setShowScrollRight(true);
                } else {
                    setShowScrollRight(false);
                }
            }
            if (isFirefox) {
                refElement.current.style.overflow = overflow;
            }
        }
    }, [refElement]);

    useEffect(() => {
        if (refElement && refElement.current) {
            setTimeout(() => checkScrollable());
            refElement.current.addEventListener("scroll", checkScrollable, { passive: true });
        }
        return () => {
            if (refElement && refElement.current) {
                refElement.current.removeEventListener("scroll", checkScrollable);
            }
        };
    }, [refElement]);

    return (
        <StyledWrapper
            css={css`
                position: relative;
                ${extendedStyles?.css}
            `}
        >
            <Transition
                mountOnEnter
                unmountOnExit
                in={showScrollLeft}
                timeout={{ enter: 75, exit: 250 }}
                nodeRef={transitionElementLeft}
            >
                {state => (
                    <ScrollIndicator direction="left" transitionState={state} color={color} shadowHeight={shadowHeight}>
                        {showArrow && <IconMemo src={ChevronLeft} size={14} />}
                    </ScrollIndicator>
                )}
            </Transition>
            <Transition
                nodeRef={transitionElementRight}
                mountOnEnter
                unmountOnExit
                in={showScrollRight}
                timeout={{ enter: 75, exit: 250 }}
            >
                {state => (
                    <ScrollIndicator
                        direction="right"
                        transitionState={state}
                        color={color}
                        shadowHeight={shadowHeight}
                    >
                        {showArrow && <IconMemo src={ChevronRight} size={14} />}
                    </ScrollIndicator>
                )}
            </Transition>
            <Transition
                nodeRef={transitionElementUp}
                transitionElement
                mountOnEnter
                unmountOnExit
                in={showScrollUp}
                timeout={{ enter: 75, exit: 250 }}
            >
                {state => (
                    <Clickable
                        onClick={() => {
                            refElement.current?.scrollBy({
                                top: scrollByValue ? -scrollByValue : 0,
                                behavior: "smooth",
                            });
                        }}
                        aria-label="arrow up"
                    >
                        <ScrollIndicator
                            direction="up"
                            transitionState={state}
                            color={color}
                            shadowHeight={shadowHeight}
                            showArrow={showArrow}
                        >
                            {showArrow && !scrollByValue && (
                                <IconMemo {...styles.showArrowUpIcon} src={IconPaginationArrowRight} />
                            )}
                            {showArrow && scrollByValue && (
                                <StyledWrapper {...styles.showArrowWrapper}>
                                    <IconMemo {...styles.showArrowUpIcon} src={IconPaginationArrowLeft} />
                                </StyledWrapper>
                            )}
                        </ScrollIndicator>
                    </Clickable>
                )}
            </Transition>
            {children}
            <Transition
                nodeRef={transitionElementDown}
                mountOnEnter
                unmountOnExit
                in={showScrollDown}
                timeout={{ enter: 75, exit: 250 }}
            >
                {state => (
                    <Clickable
                        onClick={() => {
                            refElement.current?.scrollBy({
                                top: scrollByValue ?? 0,
                                behavior: "smooth",
                            });
                        }}
                        aria-label="arrow down"
                    >
                        <ScrollIndicator
                            direction="down"
                            transitionState={state}
                            color={color}
                            shadowHeight={shadowHeight}
                            showArrow={showArrow}
                        >
                            {showArrow && !scrollByValue && (
                                <IconMemo {...styles.showArrowDownIcon} src={IconPaginationArrowRight} />
                            )}
                            {showArrow && scrollByValue && (
                                <StyledWrapper {...styles.showArrowWrapper}>
                                    <IconMemo {...styles.showArrowDownIcon} src={IconPaginationArrowLeft} />
                                </StyledWrapper>
                            )}
                        </ScrollIndicator>
                    </Clickable>
                )}
            </Transition>
        </StyledWrapper>
    );
};

export default React.forwardRef(FancyScroll);
